import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Sparkles, Loader2, RotateCcw } from 'lucide-react';
import axios from 'axios';

const STORAGE_KEY = 'bnd_chat_session';
const HISTORY_KEY = 'bnd_chat_history';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ChatbotWidget = () => {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(true);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(HISTORY_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Persist messages
  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify((Array.isArray(messages) ? messages : []).slice(-50)));
    } catch {
      // Storage full or disabled, ignore
    }
  }, [messages]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Welcome message on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content:
            "Bonjour, vous êtes en relation avec le **Service Commercial BND HOLDING**. ✨\n\nComment puis-je vous accompagner aujourd'hui ? Recherche d'un bien, conseils diaspora, calcul de mensualités…",
        },
      ]);
    }
    if (open) setUnread(false);
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open, messages.length]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/chat`, {
        session_id: sessionId,
        message: text,
      });
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            "Désolée, je rencontre un souci technique. Vous pouvez nous joindre directement via WhatsApp au +221 77 123 45 67 ou par email contact@bndholding.com.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const resetChat = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${BACKEND_URL}/api/ai/chat/${sessionId}`);
      } catch {
        // best effort
      }
    }
    setSessionId(null);
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(HISTORY_KEY);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Render assistant content with bold markdown + line breaks
  const renderContent = (text) => {
    const parts = text.split('\n');
    return (Array.isArray(parts) ? parts : []).map((line, idx) => {
      // Render **bold** segments
      const segs = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={idx} className={idx > 0 ? 'mt-1' : ''}>
          {(Array.isArray(segs) ? segs : []).map((seg, i) =>
            seg.startsWith('**') && seg.endsWith('**') ? (
              <strong key={i} className="text-[#C9A227]">{(Array.isArray(seg) ? seg : []).slice(2, -2)}</strong>
            ) : (
              <span key={i}>{seg}</span>
            )
          )}
        </p>
      );
    });
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          data-testid="chatbot-launcher"
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-6 z-50 group"
          aria-label="Ouvrir le Service Commercial"
        >
          <span className="absolute inset-0 rounded-full animate-ping bg-amber-400/40" />
          <span className="relative flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-black font-semibold shadow-2xl ring-2 ring-amber-200/60 hover:shadow-amber-300/50 transition-all hover:scale-105">
            <MessageCircle className="w-5 h-5" />
            <span className="hidden sm:inline">Service Commercial</span>
            <Sparkles className="w-4 h-4 animate-pulse" />
            {unread && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                1
              </span>
            )}
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-20 right-6 z-50 w-[92vw] max-w-md h-[70vh] max-h-[640px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border-2 border-amber-200"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#111111] to-[#080808] text-white px-5 py-4 flex items-center justify-between border-b-4 border-amber-400">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-lg ring-2 ring-white/20">
                <Sparkles className="w-5 h-5 text-black" />
              </div>
              <div>
                <p className="font-bold text-base leading-tight">Service Commercial</p>
                <p className="text-xs text-amber-300 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Conseillère en ligne
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={resetChat}
                title="Nouvelle conversation"
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="chatbot-reset-btn"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                title="Fermer"
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="chatbot-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-amber-50/40 to-white"
          >
            {(Array.isArray(messages) ? messages : []).map((m, idx) => (
              <div
                key={idx}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                    m.role === 'user'
                      ? 'bg-[#111111] text-white rounded-br-md'
                      : 'bg-white border border-amber-200 text-gray-800 rounded-bl-md'
                  }`}
                >
                  {m.role === 'assistant' ? renderContent(m.content) : m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-amber-200 rounded-2xl rounded-bl-md px-4 py-2.5 shadow-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-bounce" />
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-bounce [animation-delay:120ms]" />
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-bounce [animation-delay:240ms]" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Suggestions when empty-ish */}
          {messages.length <= 1 && !sending && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {[
                'Voir les villas et Appartement',
                'Investir depuis la diaspora',
                
              ].map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setInput(s);
                    setTimeout(() => sendMessage(), 50);
                  }}
                  className="text-xs px-3 py-1.5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-900 font-medium transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t bg-white flex items-end gap-2">
            <textarea
              ref={inputRef}
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Posez votre question…"
              rows="1"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none max-h-24"
            />
            <button
              data-testid="chatbot-send-btn"
              onClick={sendMessage}
              disabled={!input.trim() || sending}
              className="p-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-black hover:from-amber-500 hover:to-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-[10px] text-gray-400 text-center pb-1.5">
            Service Commercial BND HOLDING · Vos données restent confidentielles
          </p>
        </div>
      )}
    </>
  );
};
